import { withTheme } from '@emotion/react';
import classNames from 'classnames';
import { Record } from 'immutable';
import PropTypes from 'prop-types';
// Core
import React from 'react';
import { connect } from 'react-redux';
import compose from 'utils/compose';

import store from 'app-components/utils/redux-store';
import { accountDiscoveryActions } from 'app-components/flux/account-discovery';
import { accountsSelectors } from 'app-components/flux/accounts';
import { categoriesSelectors } from 'app-components/flux/categories';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { rootUiActions, rootUiTypes } from 'app-components/flux/root-ui';
import {
  scheduledTransactionsActions,
  scheduledTransactionsSelectors,
  scheduledTransactionsTypes,
  scheduledTransactionsUtils,
} from 'app-components/flux/scheduled-transactions';
import { tagsSelectors } from 'app-components/flux/tags';
import { transactionsSelectors } from 'app-components/flux/transactions';

import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { withStyles } from 'tss-react/mui';
import MenuItem from '@mui/material/MenuItem';

// MUI Icons
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';

// Data
import { ViewStateEnum, computeViewState } from 'containers/OverviewPage/components/helpers';

// Components
import QPureComponent from 'QPureComponent';
import { DIALOG_TYPE_REFUND_EDIT } from 'components/Dialogs/RefundEditDialog';
import { showAddScheduledTransactionDialog } from 'components/Dialogs/ScheduledTransactionAddDialog/actions';
import LoadingView from 'components/LoadingView';
import QButton from 'components/QButton';
import QDialogs from 'components/QDialogs';
import SearchBox from 'components/SearchBox';
import UIState from 'components/UIState';
import DevMenuButton from 'components/DevMenuButton';

// Assets
import underConstructionImage from 'assets/illustrations/Empty-box-disabled.png';

import ZeroStateView from 'components/ZeroStateView';
import SettingsHeader from 'containers/Settings/SharedComponents/SettingsHeader';
import RecurringRow from './RecurringRow';
import styles from './styles';

const typeEnums = scheduledTransactionsTypes.STTypeEnum;
const allValue = 'all';
const completedValue = 'COMPLETED';

// TODO: Add credit card payments AND change the selectors so that credit card payments are an acceptable type (Once credit cards are added)
class Recurring extends QPureComponent {
  constructor(props) {
    super(props);

    if (!this.props.isLoading) {
      this.props.dispatchGetScheduledTransactionsAction();
    }

    this.state = {
      globalSearch: undefined,
    };

    // Fix bug where it ALWAYS displays the txn again when changing tabs
    if (!window.location.href.includes('Recurring/')) {
      this.props.setUIState(null);
    }
  }

  componentWillUnmount() {
    this.props.setUIState({ filterTab: allValue });
  }

  txnSearch = (scheduledtxn) => {
    const { globalSearch } = this.state;

    if (!globalSearch) {
      return true;
    }

    const schTxn = this.props.schTxnsSearchObject.get(scheduledtxn.id);
    return schTxn && Object.values(schTxn).some((value) => value?.toLowerCase?.().includes(globalSearch));
  };

  searchHeader = (filterTab) => {
    switch (filterTab) {
      case allValue:
        return 'All Active';
      case typeEnums.BILL:
        return 'Bills';
      case typeEnums.SUBSCRIPTION:
        return 'Subscriptions';
      case typeEnums.INCOME:
        return 'Income';
      case typeEnums.TRANSFER:
        return 'Transfers';
      case completedValue:
        return 'Canceled';
      default:
        return 'transactions';
    }
  };

  buttonText = (filterTab, defaultString) => {
    switch (filterTab) {
      case allValue:
      case completedValue:
        return defaultString ?? 'Series';
      case typeEnums.BILL:
        return 'Bill';
      case typeEnums.SUBSCRIPTION:
        return 'Subscription';
      case typeEnums.INCOME:
        return 'Income';
      case typeEnums.TRANSFER:
        return 'Transfer';
      default:
        return 'Bill';
    }
  };

  renderAppBar = () => {
    const { classes } = this.props;

    const tabClasses = {
      root: classes.tab,
      selected: classes.selectedTab,
    };

    return (
      <div className={classes.appBar}>
        <div className={classes.appBarGridContainer}>
          <Tabs
            value={this.props.uiState.filterTab}
            onChange={(event, value) => {
              this.props.setUIState({ filterTab: value });
            }}
            indicatorColor=''
            variant='scrollable'
            scrollButtons='auto'
            ScrollButtonComponent={(scrollProps) => {
              const isLeft = scrollProps.direction === 'left';
              const clsNames = classNames(classes.tabBarArrowButtons, isLeft ? classes.arrowLeft : classes.arrowRight);

              if (!scrollProps.visible) {
                return <div className={clsNames} />;
              }

              return (
                <ButtonBase component='div' className={clsNames} onClick={scrollProps.onClick}>
                  {isLeft ? <KeyboardArrowLeft fontSize='small' /> : <KeyboardArrowRight fontSize='small' />}
                </ButtonBase>
              );
            }}
            textColor='primary'
            className={classes.mainTab}
            classes={{
              flexContainer: classes.tabBar,
            }}
          >
            <Tab label='All Active' id='all-tab' value={allValue} classes={tabClasses} />
            <Tab label='Bills' id='bills-tab' value={typeEnums.BILL} classes={tabClasses} />
            <Tab label='Subscriptions' id='subscription-tab' value={typeEnums.SUBSCRIPTION} classes={tabClasses} />
            <Tab label='Income' id='income-tab' value={typeEnums.INCOME} classes={tabClasses} />
            <Tab label='Transfers' id='transfer-tab' value={typeEnums.TRANSFER} classes={tabClasses} />
            <Tab label='Canceled' id='canceled-tab' value={completedValue} classes={tabClasses} />
          </Tabs>
        </div>
      </div>
    );
  };

  addDialog = () => {
    let props = { dialogType: this.props.uiState.filterTab }; // Filter by the selected tab
    if (props.dialogType === completedValue || props.dialogType === allValue) {
      props = undefined; // Use the default value
    }
    this.props.addScheduledTransaction(props);
  };

  refundDialog = () => {
    this.props.createRefund();
  };

  render() {
    const {
      classes,
      uiState,
      isLoading,
      isDataEmpty,
      noDependencies,
      loadPending,
      isEnabled,
      dispatchDoAccountDiscoveryAction,
      defaultButtonString,
      scheduledTransactions,
      settingsPage,
      showCreateRefundButton,
    } = this.props;
    const view = computeViewState(
      () => isLoading,
      () => isDataEmpty,
      noDependencies,
      loadPending,
      !isEnabled,
    );
    return (
      <Typography component='div' className={settingsPage ? classes.floorStyle : classes.floorStyleUpcoming}>
        <SettingsHeader
          title={settingsPage ? 'All Recurring' : ''}
          primaryItem={
            view === ViewStateEnum.MAIN && (
              <div className={classes.buttonWrapper}>
                <DevMenuButton>
                  <MenuItem
                    onClick={() => {
                      store.dispatch(
                        scheduledTransactionsActions.batchScheduledTransactions(
                          scheduledTransactions.toIndexedSeq().map((scheduledTransaction) =>
                            scheduledTransactionsTypes.mkScheduledTransactionPatch({
                              id: scheduledTransaction.id,
                              isDeleted: true,
                            }),
                          ),
                          { undo: { userMessage: 'Deleted' } },
                        ),
                      );
                    }}
                  >
                    delete all
                  </MenuItem>
                </DevMenuButton>
                {showCreateRefundButton && (
                  <QButton variant='outlinedAdd' id='add-refund' onClick={this.refundDialog}>
                    Refund
                  </QButton>
                )}
                <QButton className={classes.addButton} variant='add' id='add-bill-button' onClick={this.addDialog}>
                  {this.buttonText(this.props.uiState.filterTab, defaultButtonString)}
                </QButton>
              </div>
            )
          }
          secondaryItem={
            <SearchBox
              placeholder={`Search ${this.searchHeader(this.props.uiState.filterTab)}`}
              onSearch={(s) => this.setState({ globalSearch: s && s.toLowerCase() })}
              initialValue={this.state.globalSearch}
              autoSearch
              autoFocus
              collapsable
            />
          }
        />

        {view === ViewStateEnum.DISABLED && !isLoading && !loadPending && (
          <img
            alt='page is under construction'
            src={underConstructionImage}
            style={{
              display: 'block',
              width: '50%',
              marginTop: 40,
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          />
        )}

        {view === ViewStateEnum.LOADING && <LoadingView style={{ margin: 40 }} />}

        {view === ViewStateEnum.LOAD_PENDING && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              height: '80vh',
            }}
          >
            <Paper
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
              }}
            >
              <ZeroStateView
                title='No Data'
                qIllustration='spendingPlanBills'
                description="We can't fetch your data from the server. Please try to refresh the page."
              />
            </Paper>
          </Box>
        )}

        {view === ViewStateEnum.ZERO && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              height: '80vh',
            }}
          >
            <Paper
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
              }}
            >
              <ZeroStateView
                animate={true}
                qIllustration='spendingPlanBills'
                description="You don't have any accounts set up."
                button='Add Account'
                buttonSize='small'
                buttonVariant='contained'
                onClick={() => dispatchDoAccountDiscoveryAction()}
              />
            </Paper>
          </Box>
        )}

        {view === ViewStateEnum.EMPTY && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              height: '82vh',
            }}
          >
            <Paper
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
              }}
            >
              <ZeroStateView
                size='large'
                animate={true}
                qIllustration='billCreditcard'
                title='No recurrings to display'
                description='Set up recurring transactions to see them here.'
                button='Add Recurring'
                buttonVariant='contained'
                onClick={this.addDialog}
              />
            </Paper>
          </Box>
        )}

        {view === ViewStateEnum.MAIN && (
          <Paper variant='outlined' className={settingsPage ? classes.root : classes.upcomingRoot}>
            {this.renderAppBar()}
            {scheduledTransactions
              .filter(
                (schTxn) =>
                  (uiState.filterTab === allValue && !schTxn.isCompleted) ||
                  (uiState.filterTab === scheduledTransactionsUtils.typeFromScheduledTransaction(schTxn) &&
                    !schTxn.isCompleted) ||
                  (uiState.filterTab === completedValue && schTxn.isCompleted),
              )
              .filter(this.txnSearch)
              .toIndexedSeq()
              .sort((scheduledTransaction1, scheduledTransaction2) =>
                scheduledTransaction1.transaction.payee?.localeCompare(
                  scheduledTransaction2.transaction.payee,
                  undefined,
                  {
                    numeric: true,
                    sensitivity: 'base',
                  },
                ),
              )
              .map((schTxn, index) => (
                <RecurringRow key={schTxn.clientId || schTxn.id} model={schTxn} index={index} />
              ))}
          </Paper>
        )}
      </Typography>
    );
  }
}

Recurring.propTypes = {
  theme: PropTypes.object.isRequired,
  isEnabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  loadPending: PropTypes.bool,
  isDataEmpty: PropTypes.bool,
  settingsPage: PropTypes.bool,
  noDependencies: PropTypes.bool,
  scheduledTransactions: PropTypes.object,
  dispatchGetScheduledTransactionsAction: PropTypes.func,
  uiState: PropTypes.any,
  setUIState: PropTypes.func,
  classes: PropTypes.object.isRequired,
  dispatchDoAccountDiscoveryAction: PropTypes.func,
  tags: PropTypes.object,
  categoriesById: PropTypes.object,
  selectedId: PropTypes.string,
  addScheduledTransaction: PropTypes.func,
  createRefund: PropTypes.func,
  defaultButtonString: PropTypes.string,
  showCreateRefundButton: PropTypes.bool,
};

const mapStateToProps = (state) => {
  const scheduledTransactions = scheduledTransactionsSelectors.getUserVerifiedScheduledTransactions(state);
  const allUpcomingTxns = transactionsSelectors.getAllUpcomingTransactions(state, scheduledTransactions);
  const featureFlags = featureFlagsSelectors.getFeatureFlags(state);

  return {
    isEnabled: featureFlags.get('scheduledTransactions'),
    isLoading: scheduledTransactionsSelectors.getIsLoading(state) || featureFlagsSelectors.getIsLoading(state),
    loadPending: scheduledTransactionsSelectors.getLoadPending(state),
    isDataEmpty: Boolean(!scheduledTransactions || scheduledTransactions.size === 0),
    noDependencies: accountsSelectors.getHasReportableAccounts(state),
    scheduledTransactions,
    allUpcomingTxns,
    tags: tagsSelectors.getTags(state),
    categoriesById: categoriesSelectors.getCategoriesById(state),
    accountsById: accountsSelectors.getAccountsById(state),
    schTxnsSearchObject: scheduledTransactionsSelectors.schTxnsSearchNames(state),
  };
};

const mapDispatchToProps = (dispatch) => ({
  dispatchGetScheduledTransactionsAction: () => dispatch(scheduledTransactionsActions.getScheduledTransactions()),
  dispatchDoAccountDiscoveryAction: () =>
    dispatch(accountDiscoveryActions.doAccountDiscovery({ initiator: 'settings recurring txns' })),
  addScheduledTransaction: (args) => dispatch(showAddScheduledTransactionDialog(args)),
  createRefund: () =>
    dispatch(
      rootUiActions.createDialog(
        rootUiTypes.mkRootUiData({
          id: 'refund-card',
          type: DIALOG_TYPE_REFUND_EDIT,
          allowNesting: true,
        }),
      ),
    ),
});

const RecurringUIState = Record({
  filterTab: allValue,
});

const uiStateConfig = {
  persist: true,
  name: () => 'Recurring',
  state: () => new RecurringUIState(),
};

export default compose(
  UIState(uiStateConfig),
  withTheme,
  QDialogs(),
  connect(mapStateToProps, mapDispatchToProps),
)(withStyles(Recurring, styles));
